import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faUpload, faCheck, faSync, faTimes } from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import './ImportarProductos.css';
import moneda from '../../../moneda';
import baseURL from '../../../url';

export default function ImportarProductos({ modalImportacionVisible, setModalImportacionVisible, cargarProductos, cargarCategoria }) {
    const [pasoImportacion, setPasoImportacion] = useState(1);
    const [archivoExcel, setArchivoExcel] = useState(null);
    const [datosImportacion, setDatosImportacion] = useState([]);
    const [productosSeleccionados, setProductosSeleccionados] = useState([]);
    const [importando, setImportando] = useState(false);
    // Estado para el switch de selección
    const [todosSeleccionados, setTodosSeleccionados] = useState(false);
    // Estado para errores de validación
    const [erroresValidacion, setErroresValidacion] = useState({});
    // Estado para el toggle de actualización masiva
    const [actualizarTodo, setActualizarTodo] = useState(false);

    const cerrarModalImportacion = () => {
        setModalImportacionVisible(false);
        setPasoImportacion(1);
        setArchivoExcel(null);
        setDatosImportacion([]);
        setProductosSeleccionados([]);
        setActualizarTodo(false);
    };

    const descargarPlantilla = () => {
        const headers = [
            'idProducto', 'titulo', 'descripcion', 'precio', 'precioAnterior', 'categoria', 'masVendido', 'stock',
            'item1', 'item2', 'item3', 'item4', 'item5', 'item6', 'item7', 'item8', 'item9', 'item10'
        ];
        const ejemplo1 = [1, 'Ejemplo Producto 1', 'Descripción detallada del producto ejemplo', 15000, 20000, 'categoria1', 'si', 50, 'Característica 1', 'Característica 2', 'Característica 3', '', '', '', '', '', '', ''];
        const ejemplo2 = [2, 'Ejemplo Producto 2', 'Descripción detallada del segundo producto', 25000, 30000, 'categoria2', 'no', 25, 'Característica especial', '', '', '', '', '', '', '', '', ''];
        const vacias = Array.from({length: 48}, (_, i) => [i+3, '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '', '']);
        const data = [headers, ejemplo1, ejemplo2, ...vacias];
        const ws = XLSX.utils.aoa_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Productos');
        XLSX.writeFile(wb, 'plantilla_importar_productos.xlsx');
        toast.success('Plantilla descargada correctamente');
    };

    const procesarArchivoExcel = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const sheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[sheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
                const headers = jsonData[0];
                const productos = [];
                for (let i = 1; i < jsonData.length; i++) {
                    const row = jsonData[i];
                    if (row.some(cell => cell !== undefined && cell !== '')) {
                        const producto = {};
                        headers.forEach((header, index) => {
                            producto[header] = row[index] || '';
                        });
                        productos.push(producto);
                    }
                }
                setDatosImportacion(productos);
                setPasoImportacion(2);
                toast.success(`${productos.length} productos encontrados en el archivo`);
            } catch (error) {
                console.error('Error al procesar archivo:', error);
                toast.error('Error al procesar el archivo Excel');
            }
        };
        reader.readAsArrayBuffer(file);
    };

    const toggleProductoSeleccionado = (index) => {
        setProductosSeleccionados(prev => {
            if (prev.includes(index)) {
                return prev.filter(i => i !== index);
            } else {
                return [...prev, index];
            }
        });
    };

    const seleccionarTodos = () => {
        setProductosSeleccionados(datosImportacion.map((_, index) => index));
    };

    const deseleccionarTodos = () => {
        setProductosSeleccionados([]);
    };

    const validarProducto = (producto) => {
        const errores = {};
        // Título
        if (!producto.titulo || producto.titulo.trim().length < 5) {
            errores.titulo = 'El título debe tener al menos 5 caracteres';
        }
        // Descripción
        if (!producto.descripcion || producto.descripcion.trim().length < 5) {
            errores.descripcion = 'La descripción debe tener al menos 5 caracteres';
        }
        // Precio
        if (producto.precio === undefined || producto.precio === '' || isNaN(Number(producto.precio)) || Number(producto.precio) < 0) {
            errores.precio = 'El precio debe ser un número mayor o igual a 0';
        }
        // Precio Anterior (opcional)
        if (producto.precioAnterior !== undefined && producto.precioAnterior !== '' && (isNaN(Number(producto.precioAnterior)) || Number(producto.precioAnterior) < 0)) {
            errores.precioAnterior = 'El precio anterior debe ser un número mayor o igual a 0';
        }
        // Categoría
        if (!producto.categoria || producto.categoria.trim().length === 0) {
            errores.categoria = 'La categoría no puede estar vacía';
        }
        // Más Vendido
        if (!producto.masVendido || (producto.masVendido !== 'si' && producto.masVendido !== 'no')) {
            errores.masVendido = 'Debes seleccionar si es más vendido o no';
        }
        // Stock
        if (producto.stock === undefined || producto.stock === '' || isNaN(Number(producto.stock)) || Number(producto.stock) < 0) {
            errores.stock = 'El stock debe ser un número mayor o igual a 0';
        }
        // Item 1 (si es obligatorio)
        if (!producto.item1 || producto.item1.trim().length < 2) {
            errores.item1 = 'El item 1 debe tener al menos 2 caracteres';
        }
        return errores;
    };

    const importarProductos = async () => {
        // Solo validar productos seleccionados
        const nuevosErrores = {};
        let hayErrores = false;
        productosSeleccionados.forEach(index => {
            const producto = datosImportacion[index];
            const errores = validarProducto(producto);
            if (Object.keys(errores).length > 0) {
                nuevosErrores[index] = errores;
                hayErrores = true;
            }
        });
        setErroresValidacion(nuevosErrores);
        if (hayErrores) {
            toast.error('Corrige los errores en los productos seleccionados antes de importar.');
            return;
        }
        
        // Si es modo actualización, pedir confirmación
        if (actualizarTodo) {
            const confirmacion = await Swal.fire({
                title: '¿Estás seguro?',
                html: `
                    <div>
                        <p><strong>Se van a actualizar ${productosSeleccionados.length} productos.</strong></p>
                        <p>Esta acción es <strong>IRREVERSIBLE</strong> y sobrescribirá todos los datos de los productos seleccionados.</p>
                        <p>¿Deseas continuar?</p>
                    </div>
                `,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Sí, actualizar',
                cancelButtonText: 'Cancelar'
            });

            if (!confirmacion.isConfirmed) {
                return;
            }
        }
        
        setImportando(true);
        try {
            // Preparar productos según el modo (insertar o actualizar)
            const productosAProcesar = productosSeleccionados.map(index => {
                const producto = datosImportacion[index];
                if (actualizarTodo && producto.idProducto) {
                    // Modo actualización: incluir el ID
                    return producto;
                } else {
                    // Modo inserción: excluir el ID
                    const { idProducto, ...rest } = producto;
                    return rest;
                }
            });
            
            const endpoint = actualizarTodo ? 'productosActualizarMasivo.php' : 'productosImportarMasivo.php';
            const response = await fetch(`${baseURL}/${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ productos: productosAProcesar })
            });
            const result = await response.json();
            setImportando(false);
            if (result.resultados) {
                const exitos = result.resultados.filter(r => r.status === 'ok').length;
                const errores = result.resultados.filter(r => r.status === 'error');
                const accion = actualizarTodo ? 'actualizaron' : 'importaron';
                let mensaje = `Se ${accion} ${exitos} productos exitosamente.`;
                if (errores.length > 0) {
                    mensaje += `\n${errores.length} productos no se ${actualizarTodo ? 'actualizaron' : 'importaron'} por errores.`;
                }
                Swal.fire({
                    title: actualizarTodo ? 'Actualización finalizada' : 'Importación finalizada',
                    text: mensaje,
                    icon: exitos > 0 ? 'success' : 'warning',
                    confirmButtonText: 'Aceptar'
                }).then((result) => {
                    if (exitos > 0) {
                        // Pequeño delay para evitar conflictos con react-toastify
                        setTimeout(() => {
                            cerrarModalImportacion();
                            // Refrescar datos para mostrar los productos importados y nuevas categorías
                            cargarProductos();
                            if (cargarCategoria) {
                                cargarCategoria();
                            }
                        }, 100);
                    }
                });
            } else if (result.error) {
                toast.error(result.error);
            } else {
                toast.error('Error inesperado al importar.');
            }
        } catch (error) {
            setImportando(false);
            toast.error('Error durante la importación');
            console.error('Error:', error);
        }
    };

    const handleExcelFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            const extension = file.name.split('.').pop().toLowerCase();
            if (extension === 'xlsx' || extension === 'xls') {
                setArchivoExcel(file);
                procesarArchivoExcel(file);
            } else {
                toast.error('Por favor seleccione un archivo Excel válido (.xlsx o .xls)');
                event.target.value = '';
            }
        }
    };

    const handleInputChange = (rowIndex, field, value) => {
        setDatosImportacion(prev => prev.map((prod, idx) =>
            idx === rowIndex ? { ...prod, [field]: value } : prod
        ));
    };

    if (!modalImportacionVisible) return null;

    return (
        <div className="modalImportacion">
            <div className="modal-content-importacion">
                <div className="modal-header">
                    <div className="header-title-actions">
                        <h3>Importar Productos</h3>
                        <div className="header-actions">
                            <span className="close" onClick={cerrarModalImportacion}>&times;</span>
                        </div>
                    </div>
                </div>
                {pasoImportacion === 1 && (
                    <div className="paso-1">
                        <div className="paso-indicador">
                            <span className="paso-activo">1. Descargar Plantilla</span>
                            <span className="paso-inactivo">2. Cargar y Revisar</span>
                        </div>
                        <div className="instrucciones">
                            <h4>Instrucciones:</h4>
                            <ul>
                                <li>Descarga la plantilla Excel con la estructura correcta</li>
                                <li>Completa los datos de los productos</li>
                                <li>Guarda el archivo y súbelo en el siguiente paso</li>
                            </ul>
                        </div>
                        <div className="botones-accion">
                            <button className="btn-descargar" onClick={descargarPlantilla}>
                                <FontAwesomeIcon icon={faDownload} />
                                Descargar Plantilla
                            </button>
                            <div className="subir-archivo">
                                <label htmlFor="excel-file-import" className="btn-subir">
                                    <FontAwesomeIcon icon={faUpload} />
                                    Seleccionar Archivo Excel
                                </label>
                                <input
                                    id="excel-file-import"
                                    type="file"
                                    accept=".xlsx,.xls"
                                    onChange={handleExcelFileChange}
                                    style={{ display: 'none' }}
                                />
                            </div>
                        </div>
                        {archivoExcel && (
                            <div className="archivo-seleccionado">
                                <FontAwesomeIcon icon={faCheck} />
                                <span>Archivo seleccionado: {archivoExcel.name}</span>
                            </div>
                        )}
                    </div>
                )}
                {pasoImportacion === 2 && (
                    <div className="paso-2">
                        <div className="paso-indicador">
                            <span className="paso-completado">1. Descargar Plantilla</span>
                            <span className="paso-activo">2. Cargar y Revisar</span>
                        </div>
                        <div className="resumen-edicion">
                            <span>✏️</span>
                            <span>Puedes <strong>editar cualquier campo</strong> directamente en la tabla antes de importar. Los campos obligatorios vacíos se marcarán en rojo. Los items 2 al 10 son opcionales. <strong>Activa "Actualizar Todo" para actualizar productos existentes basándose en el ID (acción irreversible).</strong></span>
                        </div>
                        <div className="productos-preview">
                            <div className="header-preview">
                                <h4>Productos a Importar ({datosImportacion.length})</h4>
                                <div className="acciones-masivas">
                                    <label className="switch-toggle">
                                        <input
                                            type="checkbox"
                                            checked={todosSeleccionados}
                                            onChange={e => {
                                                if (e.target.checked) {
                                                    seleccionarTodos();
                                                } else {
                                                    deseleccionarTodos();
                                                }
                                                setTodosSeleccionados(e.target.checked);
                                            }}
                                        />
                                        <span className="slider"></span>
                                        <span className="switch-label">{todosSeleccionados ? 'Deseleccionar Todos' : 'Seleccionar Todos'}</span>
                                    </label>
                                    <label className="switch-toggle">
                                        <input
                                            type="checkbox"
                                            checked={actualizarTodo}
                                            onChange={e => setActualizarTodo(e.target.checked)}
                                        />
                                        <span className="slider"></span>
                                        <span className="switch-label">Actualizar Todo</span>
                                    </label>
                                </div>
                                <div className="acciones-importacion">
                                    <button 
                                        className="btn-finalizar btn-sm" 
                                        onClick={importarProductos}
                                        disabled={productosSeleccionados.length === 0 || importando}
                                    >
                                        {importando ? (
                                            <>
                                                <FontAwesomeIcon icon={faSync} spin />
                                                Importando...
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faUpload} />
                                                {actualizarTodo ? 'Actualizar' : 'Importar'} {productosSeleccionados.length} Productos
                                            </>
                                        )}
                                    </button>
                                    <button className="btn-cancelar btn-sm" onClick={cerrarModalImportacion}>
                                        <FontAwesomeIcon icon={faTimes} />
                                        Cancelar
                                    </button>
                                </div>
                            </div>
                            <div className="tabla-preview">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>
                                                <input
                                                    type="checkbox"
                                                    checked={productosSeleccionados.length === datosImportacion.length}
                                                    onChange={(e) => e.target.checked ? seleccionarTodos() : deseleccionarTodos()}
                                                />
                                            </th>
                                            <th className="id-col">ID</th>
                                            <th>Título</th>
                                            <th>Descripción</th>
                                            <th>Precio</th>
                                            <th>Precio Anterior</th>
                                            <th>Categoría</th>
                                            <th>Más Vendido</th>
                                            <th>Stock</th>
                                            <th>Item 1</th>
                                            <th>Item 2</th>
                                            <th>Item 3</th>
                                            <th>Item 4</th>
                                            <th>Item 5</th>
                                            <th>Item 6</th>
                                            <th>Item 7</th>
                                            <th>Item 8</th>
                                            <th>Item 9</th>
                                            <th>Item 10</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {datosImportacion.map((producto, index) => {
                                            // Validación de campos obligatorios con longitud mínima
                                            const isError = field => {
                                                if (!producto[field]) return true;
                                                
                                                // Validaciones específicas por campo
                                                switch(field) {
                                                    case 'titulo':
                                                    case 'descripcion':
                                                        return producto[field].trim().length < 5;
                                                    case 'precio':
                                                    case 'stock':
                                                        return isNaN(Number(producto[field])) || Number(producto[field]) < 0;
                                                    case 'masVendido':
                                                        return producto[field] !== 'si' && producto[field] !== 'no';
                                                    case 'item1':
                                                        return producto[field].trim().length < 2;
                                                    default:
                                                        return !producto[field];
                                                }
                                            };
                                            
                                            const requiredFields = ['titulo', 'descripcion', 'precio', 'categoria', 'masVendido', 'stock', 'item1'];
                                            return (
                                            <tr key={index} className={productosSeleccionados.includes(index) ? 'seleccionado' : ''}>
                                                <td>
                                                    <input
                                                        type="checkbox"
                                                        checked={productosSeleccionados.includes(index)}
                                                        onChange={() => toggleProductoSeleccionado(index)}
                                                    />
                                                </td>
                                                <td className="id-col">
                                                    <input
                                                        type="number"
                                                        value={producto.idProducto}
                                                        readOnly
                                                        className="id-input"
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={producto.titulo}
                                                        onChange={e => handleInputChange(index, 'titulo', e.target.value)}
                                                        className={((isError('titulo') || (erroresValidacion[index] && erroresValidacion[index].titulo)) ? 'input-error' : '')}
                                                    />
                                                </td>
                                                <td>
                                                    <textarea
                                                        value={producto.descripcion}
                                                        onChange={e => handleInputChange(index, 'descripcion', e.target.value)}
                                                        rows={1}
                                                        className={isError('descripcion') || (erroresValidacion[index] && erroresValidacion[index].descripcion) ? 'input-error' : ''}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        value={producto.precio}
                                                        onChange={e => handleInputChange(index, 'precio', e.target.value)}
                                                        className={isError('precio') ? 'input-error' : ''}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        value={producto.precioAnterior}
                                                        onChange={e => handleInputChange(index, 'precioAnterior', e.target.value)}
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        value={producto.categoria}
                                                        onChange={e => handleInputChange(index, 'categoria', e.target.value)}
                                                        className={isError('categoria') ? 'input-error' : ''}
                                                    />
                                                </td>
                                                <td>
                                                    <select
                                                        value={producto.masVendido}
                                                        onChange={e => handleInputChange(index, 'masVendido', e.target.value)}
                                                        className={isError('masVendido') ? 'input-error' : ''}
                                                    >
                                                        <option value="">-</option>
                                                        <option value="si">Si</option>
                                                        <option value="no">No</option>
                                                    </select>
                                                </td>
                                                <td>
                                                    <input
                                                        type="number"
                                                        value={producto.stock}
                                                        onChange={e => handleInputChange(index, 'stock', e.target.value)}
                                                        className={isError('stock') ? 'input-error' : ''}
                                                    />
                                                </td>
                                                {[...Array(10)].map((_, i) => (
                                                    <td key={i}>
                                                        <input
                                                            type="text"
                                                            value={producto[`item${i+1}`] || ''}
                                                            onChange={e => handleInputChange(index, `item${i+1}`, e.target.value)}
                                                            className={i === 0 && isError('item1') ? 'input-error' : ''}
                                                        />
                                                    </td>
                                                ))}
                                            </tr>
                                        )})}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
} 